// Étanchéité de la chaîne d'ENVOI de l'audio (DS §6.13).
//
// Trois couches produisent des textes techniques ici, et aucune n'écrit pour
// l'usagère :
//
// 1. le serveur TUS de l'hébergeur du stockage — « The object exceeded the
//    maximum allowed size » (plafond de 50 Mo du plan, ≈ 2 h d'audio) ;
// 2. la couche réseau de la WKWebView — « TypeError: Load failed » sur iOS,
//    « Failed to fetch » ailleurs ;
// 3. le module d'enregistrement natif, quand le fichier ne se lit plus.
//
// `friendlyNetworkError` (`sync-capture.ts`) traduisait le cas 2 et RETOMBAIT
// SUR LE TEXTE BRUT pour tout le reste — c'est le défaut que le DS nomme : une
// traduction par cas n'est pas une étanchéité. Ici la reconnaissance existe
// (deux motifs stables dont le GESTE diffère, comme le kill-switch budgétaire
// de `invoke-error.ts`), mais le repli est une phrase à nous : le texte brut ne
// ressort jamais, motif connu ou pas.
//
// Toutes ces phrases disent ce qui est CONSERVÉ (§6.11) : l'audio est encore sur
// l'appareil, l'envoi est réessayable. Un échec qui détient une donnée de
// l'usager doit le dire.

import { isAuthRetryableFetchError } from "@supabase/supabase-js";
import { logDbError } from "../supabase/db-error";

// Le seul cas dont le geste diffère vraiment : réessayer ne marchera JAMAIS
// pour ce fichier — il dépasse le plafond de l'hébergeur.
export const AUDIO_TROP_VOLUMINEUX =
  "Cet enregistrement est trop volumineux pour être envoyé (environ 2 heures au maximum). Il reste sur cet appareil : écris-nous, on peut le récupérer autrement.";

// Formulation conservée telle quelle : la QA fonctionnelle l'a déjà lue et
// validée, et le recensement la donne en exemple du patron à imiter.
export const RESEAU_PERDU =
  "Connexion réseau perdue. Vérifie ta connexion, puis réessaie la synchro.";

export const AUDIO_NON_ENVOYE =
  "L'envoi de l'audio n'a pas abouti. Il est conservé sur cet appareil — touche « Réessayer la synchro ».";

export const AUDIO_NON_LU =
  "L'enregistrement audio n'a pas pu être lu sur cet appareil. Réessaie la synchro ; si ça persiste, écris-nous.";

export const AUDIO_INTROUVABLE_SUR_APPAREIL =
  "L'enregistrement audio est introuvable sur cet appareil : il ne peut pas être envoyé. Écris-nous si cette rencontre compte.";

// Texte existant (autrefois écrit en ligne dans `upload.ts`) : le seul échec
// dont le geste est « se reconnecter ». Nommé pour que l'écran de capture le
// reconnaisse (C20) sans comparer une chaîne recopiée.
export const SESSION_EXPIREE = "Session expirée : reconnecte-toi pour téléverser l'audio.";

export const CAPTURE_NON_ENREGISTREE =
  "La capture n'a pas pu être enregistrée sur le serveur. L'audio reste sur cet appareil — touche « Réessayer la synchro ».";

const RESEAU_PATTERN = /load failed|failed to fetch|network ?error/i;
// Envoi TUS sans AUCUNE réponse HTTP (`DetailedError` de tus-js-client) : la
// requête n'a pas atteint le serveur — c'est une panne réseau, pas un refus.
const TUS_SANS_REPONSE_PATTERN = /response code: n\/a/i;
// Passerelle ou base momentanément injoignable (502, 503, 504), vu par TUS
// (« response code: 503 ») ou par la base (« HTTP 503 … », `httpDetail`) :
// réessayer plus tard réussira. Les autres 5xx restent des défauts.
const PASSERELLE_PATTERN = /(?:response code: |\bHTTP )50[234]\b/i;
const TROP_GROS_PATTERN = /exceeded the maximum allowed size|entity too large|413/i;

/**
 * Motif technique d'une panne RÉSEAU (même critère que `friendlyNetworkError`,
 * plus l'envoi TUS resté sans réponse et les réponses 502/503/504) : le réseau ou la base était injoignable,
 * réessayer plus tard réussira. Tout le reste est un refus ou un défaut.
 */
export function isNetworkMotif(detail: string): boolean {
  return (
    RESEAU_PATTERN.test(detail) ||
    TUS_SANS_REPONSE_PATTERN.test(detail) ||
    PASSERELLE_PATTERN.test(detail)
  );
}

/**
 * Motif technique d'une erreur de la base, avec le statut HTTP de la réponse
 * quand il est connu (« HTTP 503 … ») — pour reconnaître une passerelle
 * momentanément injoignable. Jamais affiché.
 */
export function httpDetail(message: string, status: number | undefined): string {
  return typeof status === "number" && status > 0 ? `HTTP ${status} ${message}` : message;
}

/**
 * Échec d'une lecture de session qui rend une session NULLE (`getSession`).
 * Jeton d'accès expiré dont le rafraîchissement échoue sur le réseau ou sur
 * 502/503/504 : auth-js rend une session nulle AVEC une
 * `AuthRetryableFetchError`, sans lever, et garde la session stockée. Ce n'est
 * pas une déconnexion : réessayer plus tard réussira (C20, tour 4). Sans
 * erreur réessayable : session expirée, le geste est de se reconnecter.
 */
export function missingSessionFailure(
  scope: string,
  error: unknown,
): { ok: false; error: string; detail?: string } {
  if (isAuthRetryableFetchError(error)) {
    const detail = httpDetail(`${error.name}: ${error.message}`, error.status);
    logDbError(scope, detail);
    return { ok: false, error: RESEAU_PERDU, detail };
  }
  return { ok: false, error: SESSION_EXPIREE };
}

/**
 * Journalise le motif technique d'un échec d'envoi et rend la phrase à
 * afficher. Deux cas reconnus parce que leur GESTE diffère ; tout le reste
 * ressort dans nos mots, y compris un motif jamais vu.
 */
export function uploadErrorMessage(scope: string, detail: string): string {
  logDbError(scope, detail);
  if (TROP_GROS_PATTERN.test(detail)) return AUDIO_TROP_VOLUMINEUX;
  if (RESEAU_PATTERN.test(detail)) return RESEAU_PERDU;
  return AUDIO_NON_ENVOYE;
}
